import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarItem from "./sidebar/SidebarItem";
import { menuItems } from "./sidebar/menuItems";
import { SidebarProps, MenuItem } from "./sidebar/types";
import {
  mapPrimaryIdToPath,
  mapLabelToPath,
  mapChildLabelToPath,
} from "./sidebar/helpers";

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Khởi tạo state từ localStorage hoặc mặc định là null
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(() => {
    const saved = localStorage.getItem("sidebarOpenSubmenu");
    return saved || null;
  });

  const [openChildSubmenu, setOpenChildSubmenu] = useState<string | null>(
    () => {
      const saved = localStorage.getItem("sidebarOpenChildSubmenu");
      return saved || null;
    }
  );

  const toggleSubmenu = (id: string) => {
    // Đóng cấp 2 và chuyển trạng thái ngay để đóng/mở cùng lúc
    setOpenChildSubmenu(null);
    localStorage.removeItem("sidebarOpenChildSubmenu");

    const newState = openSubmenu === id ? null : id;
    setOpenSubmenu(newState);

    // Lưu vào localStorage
    if (newState) {
      localStorage.setItem("sidebarOpenSubmenu", newState);
    } else {
      localStorage.removeItem("sidebarOpenSubmenu");
    }
  };

  const toggleChildSubmenu = (id: string) => {
    const newState = openChildSubmenu === id ? null : id;
    setOpenChildSubmenu(newState);

    // Lưu vào localStorage
    if (newState) {
      localStorage.setItem("sidebarOpenChildSubmenu", newState);
    } else {
      localStorage.removeItem("sidebarOpenChildSubmenu");
    }
  };

  // Hàm kiểm tra menu item có active không (chỉ active chính xác trang đó)
  const isMenuItemActive = (item: MenuItem): boolean => {
    const currentPath = location.pathname;

    // Chỉ kiểm tra primary menu nếu không có submenu
    if (!item.subItems || item.subItems.length === 0) {
      const primaryPath = mapPrimaryIdToPath(item.id);
      return currentPath === primaryPath;
    }

    // Nếu có submenu thì không active primary menu
    return false;
  };

  // Hàm kiểm tra submenu item có active không
  const isSubItemActive = (parentId: string, label: string): boolean => {
    const subPath = mapLabelToPath(parentId, label);
    return subPath ? location.pathname === subPath : false;
  };

  // Hàm kiểm tra child submenu item có active không
  const isChildItemActive = (
    parentId: string,
    subParentId: string,
    label: string
  ): boolean => {
    const childPath = mapChildLabelToPath(parentId, subParentId, label);
    return childPath ? location.pathname === childPath : false;
  };

  // Đóng submenu khi sidebar thu gọn
  React.useEffect(() => {
    if (isCollapsed) {
      // Delay để đóng submenu sau khi sidebar thu gọn xong
      const timer = setTimeout(() => {
        setOpenSubmenu(null);
        setOpenChildSubmenu(null);
        // Xóa khỏi localStorage khi sidebar thu gọn
        localStorage.removeItem("sidebarOpenSubmenu");
        localStorage.removeItem("sidebarOpenChildSubmenu");
      }, 300); // 300ms = duration của sidebar transition

      return () => clearTimeout(timer);
    }
  }, [isCollapsed]);

  return (
    <aside
      className={`${
        isCollapsed ? "w-16" : "w-72"
      } bg-[#fcfcfc] fixed left-0 top-16 bottom-0 border-r border-gray-200 flex flex-col transition-all duration-300 pt-2`}
    >
      <nav className="flex-1 overflow-y-auto">
        <ul className="space-y-0">
          {menuItems.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              isCollapsed={isCollapsed}
              openSubmenu={openSubmenu}
              openChildSubmenu={openChildSubmenu}
              isActive={isMenuItemActive(item)}
              isSubItemActive={isSubItemActive}
              isChildItemActive={isChildItemActive}
              onToggleSubmenu={(id) => toggleSubmenu(id)}
              onToggleChildSubmenu={(id) => toggleChildSubmenu(id)}
              onPrimaryClick={(menuItem: MenuItem, hasSubmenu: boolean) => {
                if (hasSubmenu && !isCollapsed) {
                  toggleSubmenu(menuItem.id);
                } else if (!hasSubmenu) {
                  navigate(mapPrimaryIdToPath(menuItem.id));
                }
              }}
              onSubLabelClick={(sectionId, label) => {
                const path = mapLabelToPath(sectionId, label);
                if (path) navigate(path);
              }}
              onChildLabelClick={(sectionId, parentId, label) => {
                const path = mapChildLabelToPath(sectionId, parentId, label);
                if (path) navigate(path);
              }}
            />
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
